import "./MissionClientForm.scss";
import InfoContext from "pages/InfoContext";
import InputCheckList from "core/inputs/input-check-list/InputCheckList";
import { FormattedMessage } from "react-intl";
import {
  MISSION_FORM_LABELS,
  MISSION_FORM_PLACEHOLDERS,
  RULES,
} from "constants/forms";
import { Typography } from "core/typography/Typography";
import { getCorrespondingTag } from "utils/missionHelper";
import { useContext, useEffect, useRef, useCallback } from "react";
import { useUserContext } from "api/user/UserProvider";
import { useWindowSize } from "utils/customHooks";
import { desktopSize } from "utils/screenSizes";
import {
  LANG,
  REMOTE_WORK_TAGS,
  TAD_DAYS,
  TAG_TYPES,
} from "constants/constants";
import {
  renderInputAffiliate,
  renderInputAddress,
  renderTADSelect,
  renderInputPurchasingContact,
  renderInputClientContact,
  renderInputCoordinator,
  renderInputRemoteDays,
  renderPhonenumber,
  renderInputLinkedin,
} from "../CommonInputs";
import propTypes from "prop-types";

const MissionClientForm = ({
  control,
  errors,
  register,
  setValue,
  watch,
  unregister,
  clearErrors,
}) => {
  const {
    getClients,
    clientsList,
    personsList,
    getOptions,
    getNamesByFilters,
    namesByFilters,
    dataTTV,
    readOnly,
    cellHighlight,
    isValidated,
    clientById,
    getClientById,
    apiErrorResponse,
    isAuthorizedManager,
  } = useContext(InfoContext);

  const inputClientName = watch("client_id", []);
  const rate_coordinator = watch("rate_coordinator", []);
  const address = watch("address", []);
  const remote_work = watch("remote_work_id", null);
  const remote_work_days = watch("remote_work_days", []);
  const { lang } = useUserContext();
  const remote_work_id = watch("remote_work_id", []);
  const size = useWindowSize();
  const lastProcessedClientId = useRef(inputClientName?.[0]?.value || null);

  const clearValues = () => {
    const defaultValues = {
      rate_coordinator: [],
      address: "",
      client_contact: "",
      client_contact_email: "",
      client_contact_phonenumber: "",
      purchasing_contact: "",
      client_contact_linkedin: "",
      remote_work_id: null,
      non_remote_days_number: null,
      remote_work_days: null,
    };

    Object.entries(defaultValues).forEach(([field, value]) => {
      setValue(field, value);
    });
  };

  const processClientData = useCallback(() => {
    if (inputClientName[0]?.value && clientById && !readOnly) {
      const clientId = inputClientName[0]?.value;

      if (
        clientById?.id === clientId &&
        lastProcessedClientId.current !== clientId
      ) {
        const fields = {
          address: [
            clientById?.address?.replace(/\r?\n/g, " "),
            clientById?.postal_code + " " + clientById?.city,
            clientById?.country,
          ]
            .filter(Boolean)
            .join(", "),
          client_contact_email: clientById.email,
          client_contact: clientById.contact_name,
          client_contact_phonenumber: clientById.phone,
          client_contact_linkedin: clientById.linkedin,
        };

        Object.entries(fields).forEach(([key, value]) => {
          if (value) setValue(key, value);
        });

        lastProcessedClientId.current = clientId;
      }
    }
  }, [inputClientName, clientById, readOnly, setValue]);

  useEffect(() => {
    if (inputClientName[0]?.value && !readOnly) {
      getClientById({
        id: inputClientName[0]?.value,
      });
    }
  }, [inputClientName]);

  useEffect(() => {
    processClientData();
  }, [clientById]);

  const buildTADDays = () => (
    <InputCheckList
      onChange={(vals) => setValue("remote_work_days", vals)}
      value={watch("remote_work_days")}
      infoList={TAD_DAYS}
      validation={isValidated && remote_work_days?.length === 0}
      readOnly={readOnly}
      errors={errors}
      errorMessage={null}
      label={"tad_required_by_client"}
      errorValue={null}
    />
  );

  useEffect(() => {
    switch (remote_work_id) {
      case 1:
        setValue("remote_work_days", null);
        setValue("non_remote_days_number", null);
        unregister("remote_work_days");
        unregister("non_remote_days_number");
        break;
      case 2:
        setValue("remote_work_days", null);
        unregister("remote_work_days");
        break;
      case 3:
        setValue("non_remote_days_number", null);
        unregister("non_remote_days_number");
        break;
      default:
        break;
    }
  }, [remote_work_id]);

  const renderInputsByScreenSize = () => {
    return (
      <div
        className={`cell-padding ${
          (!address && cellHighlight) ||
          errors?.["address"] ||
          apiErrorResponse?.["address"]
            ? "errorCellColor"
            : ""
        }`}
      >
        {renderInputAddress({
          control,
          width: "100%",
          errors,
          label: MISSION_FORM_LABELS.intervention_address,
          placeholder: MISSION_FORM_LABELS.intervention_address,
          readOnly,
          register,
          setValue,
          value: watch("address") || [],
          id: MISSION_FORM_LABELS.intervention_address.id,
          errorMessage:
            apiErrorResponse?.["address"] ||
            errors?.["address"]?.message ||
            null,
          errorValue: null,
          rows: 1,
          dynamicHeight: true,
          defaultDynamicHeight: "54px",
        })}
      </div>
    );
  };

  return (
    <div className="client-infos-wrapper" style={{ marginTop: "4rem" }}>
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={<FormattedMessage id="client_infos" defaultMessage="CLIENT" />}
      />
      <div className="section-1">
        <div className="line-1">
          <div
            className={`cell-padding ${
              (inputClientName.length === 0 && cellHighlight) ||
              errors?.["client_id"] ||
              apiErrorResponse?.["client_id"]
                ? "errorCellColor"
                : ""
            }`}
          >
            {renderInputAffiliate({
              clearErrors,
              errorMessage:
                apiErrorResponse?.["client_id"] ||
                errors?.["client_id"]?.message ||
                null,
              errorValue: null,
              control,
              errors: errors["client_id"] || apiErrorResponse?.["client_id"],
              getOptions: (search) => getClients({ search }),
              label: MISSION_FORM_LABELS.client_id,
              clientsList: clientsList?.map((elm) => ({
                label: elm?.name,
                value: elm?.id,
                type: TAG_TYPES.CLIENTS,
              })),
              readOnly,
              required: true,
              register,
              placeholder: MISSION_FORM_LABELS.client_id,
              setValue,
              name: "client_id",
              value: inputClientName || [],
              onDelete: () => {
                setValue("client_id", "");
                clearValues();
                lastProcessedClientId.current = null;
              },
              isAuthorizedManager,
            })}
          </div>
          <div className="cell-padding">
            {renderInputCoordinator({
              control,
              errors,
              getNamesByFilters,
              label: MISSION_FORM_LABELS.rate_coordinator,
              placeholder: MISSION_FORM_LABELS.rate_coordinator,
              namesByFilters,
              readOnly,
              register,
              setValue,
              value: rate_coordinator,
            })}
          </div>
          {size.width > desktopSize && renderInputsByScreenSize()}
        </div>
        <div className="line-2">
          <div className="cell-padding">
            {renderInputClientContact({
              control,
              errors,
              label: MISSION_FORM_LABELS.client_contact,
              placeholder: MISSION_FORM_LABELS.client_contact,
              readOnly,
              register,
              required: false,
              setValue,
              name: "client_contact",
              value: watch("client_contact"),
            })}
          </div>
          <div
            className={`cell-padding ${
              (!watch("client_contact_email") && cellHighlight) ||
              errors?.["client_contact_email"] ||
              apiErrorResponse?.["client_contact_email"]
                ? "errorCellColor"
                : ""
            }`}
          >
            {renderInputClientContact({
              control,
              errors,
              label: MISSION_FORM_LABELS.client_contact_email,
              placeholder: MISSION_FORM_LABELS.client_contact_email,
              readOnly,
              required: true,
              register,
              setValue,
              name: "client_contact_email",
              value: watch("client_contact_email"),
              pattern: RULES.email,
              patternMessage: "invalid_email",
            })}
          </div>
          <div className="cell-padding">
            {renderPhonenumber({
              errorMessage: apiErrorResponse?.["client_contact_phonenumber"],
              control,
              errors,
              label: MISSION_FORM_LABELS.client_contact_phonenumber,
              placeholder: MISSION_FORM_PLACEHOLDERS.client_contact_phonenumber,
              readOnly,
              register,
              required: false,
              name: "client_contact_phonenumber",
              value: watch("client_contact_phonenumber"),
            })}
          </div>
          <div className="cell-padding">
            {renderInputPurchasingContact({
              control,
              errors,
              getOptions,
              label: MISSION_FORM_LABELS.purchasing_contact,
              personsList,
              placeholder: MISSION_FORM_LABELS.purchasing_contact,
              readOnly,
              register,
              setValue,
              value: watch("purchasing_contact"),
            })}
          </div>
          <div
            className={`cell-padding ${
              (!watch("client_contact_linkedin") && cellHighlight) ||
              errors?.["client_contact_linkedin"] ||
              apiErrorResponse?.["client_contact_linkedin"]
                ? "errorCellColor"
                : ""
            }`}
          >
            {renderInputLinkedin({
              control,
              errors,
              label: MISSION_FORM_LABELS.client_contact_linkedin,
              placeholder: MISSION_FORM_PLACEHOLDERS.client_contact_linkedin,
              readOnly,
              register,
              setValue,
              required: true,
              value: watch("client_contact_linkedin"),
              errorMessage:
                apiErrorResponse?.["client_contact_linkedin"] ||
                errors?.["client_contact_linkedin"]?.message ||
                null,
            })}
          </div>
          <div className="line-3">
            {size.width <= desktopSize && renderInputsByScreenSize()}
            <div
              className={`cell-padding ${
                !remote_work && cellHighlight && "errorCellColor"
              }`}
            >
              {renderTADSelect({
                control,
                errors,
                label: MISSION_FORM_LABELS.TAD,
                placeholder: MISSION_FORM_LABELS.TAD,
                options: dataTTV?.map((elm) => ({
                  label: lang === LANG.FR ? elm.fr : elm.en,
                  value: elm.id,
                })),
                readOnly,
                register,
                setValue,
                value: remote_work_id,
              })}
            </div>
            {getCorrespondingTag({ list: dataTTV, id: remote_work_id }) ===
              REMOTE_WORK_TAGS.CLIENT_IMPOSED && (
              <div
                className={`cell-padding ${
                  remote_work_days?.length === 0 &&
                  cellHighlight &&
                  "errorCellColor"
                }`}
              >
                <div>{buildTADDays()}</div>
              </div>
            )}
            {getCorrespondingTag({ list: dataTTV, id: remote_work_id }) ===
              REMOTE_WORK_TAGS.VOLUNTARY && (
              <div
                className={`cell-padding remote_days ${
                  !watch("non_remote_days_number") &&
                  cellHighlight &&
                  "errorCellColor"
                }`}
              >
                {renderInputRemoteDays({
                  control,
                  errors,
                  errorMessage: null,
                  errorValue: null,
                  readOnly,
                  register,
                  setValue,
                  value: watch("non_remote_days_number"),
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionClientForm;

MissionClientForm.propTypes = {
  control: propTypes.object,
  errors: propTypes.object,
  register: propTypes.func,
  setValue: propTypes.func,
  watch: propTypes.func,
  unregister: propTypes.func,
};
